// path: frontend/src/constants/templates.ts
// Background templates with tier classification for 3-tier plan system

export type BackgroundTier = "standard" | "premium" | "animated";

export interface BgTemplate {
  id: string;
  label: string;
  tier: BackgroundTier; // 🔒 Tier for plan restrictions
}

// 🔒 Background templates with tier classification
// IDs must match actual filenames in backend/static/templates/
export const BACKGROUND_TEMPLATES: BgTemplate[] = [
  // ============ STANDARD TIER (Free, Starter, Pro) ============
  // DNA themed
  { id: "DNA_Helix_Motion-vertical.mp4", label: "DNA – Vertical", tier: "standard" },
  { id: "DNA_Helix_Motion-Landscape.mp4", label: "DNA – Landscape", tier: "standard" },
  { id: "DNA_Helix_Motion-Square.mp4", label: "DNA – Square", tier: "standard" },

  // Cardio themed
  { id: "cardiologyqzVertical.mp4", label: "Cardio – Vertical", tier: "standard" },
  { id: "cardiologyqzLandscape.mp4", label: "Cardio – Landscape", tier: "standard" },
  { id: "cardiologyqzSquare.mp4", label: "Cardio – Square", tier: "standard" },

  // Pharma themed
  { id: "pharmaVertical.mp4", label: "Pharma – Vertical", tier: "standard" },
  { id: "pharmaLandscape.mp4", label: "Pharma – Landscape", tier: "standard" },
  { id: "pharmaSquare.mp4", label: "Pharma – Square", tier: "standard" },

  // Micro themed
  { id: "microVertical.mp4", label: "Micro – Vertical", tier: "standard" },
  { id: "microLandscape.mp4", label: "Micro – Landscape", tier: "standard" },
  { id: "microSquare.mp4", label: "Micro – Square", tier: "standard" },

  // ============ PREMIUM TIER (Starter, Pro) ============
  // Viro themed
  { id: "viroVertical.mp4", label: "Viro – Vertical", tier: "premium" },
  { id: "viroLandscape.mp4", label: "Viro – Landscape", tier: "premium" },
  { id: "viroSquare.mp4", label: "Viro – Square", tier: "premium" },

  // Gyneco themed
  { id: "gynecoVertical.mp4", label: "Gyneco – Vertical", tier: "premium" },
  { id: "gynecoLandscape.mp4", label: "Gyneco – Landscape", tier: "premium" },
  { id: "gynecoSquare.mp4", label: "Gyneco – Square", tier: "premium" },

  // Pathology themed
  { id: "pathoVertical.mp4", label: "Pathology – Vertical", tier: "premium" },
  { id: "pathoLandscape.mp4", label: "Pathology – Landscape", tier: "premium" },
  { id: "pathoSquare.mp4", label: "Pathology – Square", tier: "premium" },

  // Anatomy themed
  { id: "anatomyVertical.mp4", label: "Anatomy – Vertical", tier: "premium" },
  { id: "anatomyLandscape.mp4", label: "Anatomy – Landscape", tier: "premium" },
  { id: "anatomySquare.mp4", label: "Anatomy – Square", tier: "premium" },

  // Hemato themed - ✅ FIXED: lowercase 'l' in landscape
  { id: "hematoVertical.mp4", label: "Hemato – Vertical", tier: "premium" },
  { id: "hematolandscape.mp4", label: "Hemato – Landscape", tier: "premium" },
  { id: "hematoSquare.mp4", label: "Hemato – Square", tier: "premium" },

  // Biostat themed - ✅ FIXED: uppercase 'L'
  { id: "biostatVertical.mp4", label: "Biostat – Vertical", tier: "premium" },
  { id: "biostatLandscape.mp4", label: "Biostat – Landscape", tier: "premium" },
  { id: "biostatSquare.mp4", label: "Biostat – Square", tier: "premium" },

  // Neuro themed - ✅ FIXED: uppercase 'L'
  { id: "neuroVertical.mp4", label: "Neuro – Vertical", tier: "premium" },
  { id: "neuroLandscape.mp4", label: "Neuro – Landscape", tier: "premium" },
  { id: "neuroSquare.mp4", label: "Neuro – Square", tier: "premium" },
];

// Note: Animated backgrounds (tier: "animated") are dynamically generated
// and returned from the backend /api/video/background/templates endpoint.

export const DEFAULT_BACKGROUND =
  BACKGROUND_TEMPLATES[0]?.id ?? "DNA_Helix_Motion-vertical.mp4";

// ✅ List of known animated background style names
// These match the styles available in backend/app/services/animated_background.py
export const ANIMATED_BACKGROUND_STYLES = [
  "med_unified_blue",
  "neon_waves",
  "particles_soft",
  "gradient_pan",
  "med_pathology",
  "med_biochemistry",
  "med_physiology",
  "med_microbiology",
  "med_pharmacology",
  "med_biostatistics",
  "med_public_health",
] as const;

// ✅ Helper to detect if a background is an animated one
export function isAnimatedBackground(bgId: string): boolean {
  if (!bgId) return false;

  // Check if it's in the /static/assets/generated/ directory (animated backgrounds)
  if (bgId.includes("/assets/generated/")) {
    return true;
  }

  // Check if the filename matches known animated background patterns
  // Format: {style}_{width}x{height}_{fps}fps_{duration}s.mp4
  // Example: med_unified_blue_720x1280_30fps_6s.mp4
  const animatedPattern = /^(med_|neon_|particles_|gradient_).*_\d+x\d+_\d+fps_\d+s\.mp4$/;
  const filename = bgId.split("/").pop() || bgId;
  if (animatedPattern.test(filename)) {
    return true;
  }

  // Check if it's a known animated style name
  if (ANIMATED_BACKGROUND_STYLES.some((style) => bgId.includes(style))) {
    return true;
  }

  return false;
}

// 🔒 Helper to get tier for a background ID
export function getBackgroundTier(bgId: string): BackgroundTier {
  if (!bgId) return "standard";

  // ✅ Check if it's an animated background first
  if (isAnimatedBackground(bgId)) {
    return "animated";
  }

  // Check template backgrounds
  const template = BACKGROUND_TEMPLATES.find((t) => t.id === bgId);
  return template?.tier || "standard";
}

// 🔒 Helper to check if tier is allowed for plan
export function isTierAllowedForPlan(
  tier: BackgroundTier,
  plan: "free" | "starter" | "pro",
): boolean {
  if (plan === "pro") return true; // Pro: all tiers
  if (plan === "starter" && tier !== "animated") return true; // Starter: standard + premium
  if (plan === "free" && tier === "standard") return true; // Free: standard only
  return false;
}

// ✅ Helper to check if a specific background is allowed for a plan
export function isBackgroundAllowedForPlan(
  bgId: string,
  plan: "free" | "starter" | "pro",
): boolean {
  const tier = getBackgroundTier(bgId);
  return isTierAllowedForPlan(tier, plan);
}

// ✅ Helper to filter backgrounds by plan
export function getAvailableBackgrounds(plan: "free" | "starter" | "pro"): BgTemplate[] {
  return BACKGROUND_TEMPLATES.filter((template) =>
    isTierAllowedForPlan(template.tier, plan),
  );
}

// ✅ Helper to get display name for a background
export function getBackgroundDisplayName(bgId: string): string {
  if (!bgId) return "Default";

  // Static templates
  const template = BACKGROUND_TEMPLATES.find((t) => t.id === bgId);
  if (template) return template.label;

  // Animated: derive name from filename / style
  if (isAnimatedBackground(bgId)) {
    const filename = bgId.split("/").pop() || bgId;
    const match = filename.match(/^([a-z_]+?)_\d+x\d+/);

    if (match && match[1]) {
      const styleName = match[1];
      return styleName
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    }

    return "Custom Animated";
  }

  // Fallback: filename without extension
  const filename = bgId.split("/").pop() || bgId;
  return filename.replace(/\.[^/.]+$/, "");
}

// ✅ Helper to get thumbnail path for a background
// Thumbnails are served by frontend /public/templates/*
export function getBackgroundThumbnail(bgId: string): string {
  if (!bgId) return "/templates/dna_vert.jpg"; // Default thumbnail

  // Map video filenames to thumbnail image filenames
  const thumbnailMap: Record<string, string> = {
    "DNA_Helix_Motion-vertical.mp4": "dna_vert.jpg",
    "DNA_Helix_Motion-Landscape.mp4": "dna_land.jpg",
    "DNA_Helix_Motion-Square.mp4": "dna_sq.jpg",
    "cardiologyqzVertical.mp4": "cardio_vert.jpg",
    "cardiologyqzLandscape.mp4": "cardio_land.jpg",
    "cardiologyqzSquare.mp4": "cardio_sq.jpg",
    "pharmaVertical.mp4": "pharma_vert.jpg",
    "pharmaLandscape.mp4": "pharma_land.jpg",
    "pharmaSquare.mp4": "pharma_sq.jpg",
    "microVertical.mp4": "micro_vert.jpg",
    "microLandscape.mp4": "micro_land.jpg",
    "microSquare.mp4": "micro_sq.jpg",
    "viroVertical.mp4": "viro_vert.jpg",
    "viroLandscape.mp4": "viro_land.jpg",
    "viroSquare.mp4": "viro_sq.jpg",
    "gynecoVertical.mp4": "gyneco_vert.jpg",
    "gynecoLandscape.mp4": "gyneco_land.jpg",
    "gynecoSquare.mp4": "gyneco_sq.jpg",
    "pathoVertical.mp4": "patho_vert.jpg",
    "pathoLandscape.mp4": "patho_land.jpg",
    "pathoSquare.mp4": "patho_sq.jpg",
    "anatomyVertical.mp4": "anatomy_vert.jpg",
    "anatomyLandscape.mp4": "anatomy_land.jpg",
    "anatomySquare.mp4": "anatomy_sq.jpg",
    "hematoVertical.mp4": "hemato_vert.jpg",
    "hematolandscape.mp4": "hemato_land.jpg", // ✅ FIXED: lowercase 'l'
    "hematoSquare.mp4": "hemato_sq.jpg",
    "biostatVertical.mp4": "biostat_vert.jpg",
    "biostatLandscape.mp4": "biostat_land.jpg",
    "biostatSquare.mp4": "biostat_sq.jpg",
    "neuroVertical.mp4": "neuro_vert.jpg",
    "neuroLandscape.mp4": "neuro_land.jpg",
    "neuroSquare.mp4": "neuro_sq.jpg",
  };

  const thumbnailFile = thumbnailMap[bgId];
  return thumbnailFile ? `/templates/${thumbnailFile}` : "/templates/dna_vert.jpg";
}